/**
 * Add-to-Home-Screen support: capturing the browser's install prompt, deciding
 * when to offer it, and pacing the once-per-day in-app promotion.
 *
 * Three very different platforms:
 *  - **Chromium** fires `beforeinstallprompt`, which we stash so the app can
 *    trigger the native sheet from its own UI (the event is single-use).
 *  - **iOS / iPadOS** has no programmatic install at all — the only route is
 *    the Share sheet, so there we show instructions instead.
 *  - **macOS Safari** (17+) likewise has no prompt API; its manual route is
 *    Share → Add to Dock, so it gets its own instruction set.
 */
import { computed, reactive, ref, type ComputedRef } from 'vue';
import { Capacitor } from '@capacitor/core';
import { isAppleTouchUa } from '@/utils/device';

/** Not in lib.dom yet — Chromium-only. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const deferred = ref<BeforeInstallPromptEvent | null>(null);
const installedThisSession = ref(false);

/** True when the app is running as an installed PWA rather than in a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const displayMode = window.matchMedia?.('(display-mode: standalone)').matches === true;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return displayMode || iosStandalone;
}

/**
 * iOS / iPadOS browser outside the native shell. Modern iPads report a
 * desktop-class "Macintosh" UA, so a touch check is the cheapest reliable
 * way to tell them apart from actual Macs — the rule lives once, in
 * `isAppleTouchUa()` (src/utils/device.ts), shared with the mobile-only gate.
 */
export function isIosBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  const pointerCoarse =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches;
  const ios = isAppleTouchUa(navigator.userAgent, navigator.maxTouchPoints, pointerCoarse);
  return ios && !Capacitor.isNativePlatform();
}

/** Real macOS Safari (not Chromium/Edge/Opera on a Mac, not a desktop-UA iPad). */
export function isMacSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return (
    /Macintosh/.test(ua) &&
    /Safari/.test(ua) &&
    !/Chrome|Chromium|Edg|OPR/.test(ua) &&
    navigator.maxTouchPoints <= 1 &&
    !Capacitor.isNativePlatform()
  );
}

/** A native install prompt is available right now. */
export const canInstall = computed(() => deferred.value !== null && !installedThisSession.value);

/** No native prompt, but we can explain the manual Add-to-Home-Screen steps. */
export const canShowIosGuide = computed(() => isIosBrowser() && !isStandalone());

/** No native prompt, but macOS Safari can Add to Dock manually. */
export const canShowMacGuide = computed(() => isMacSafari() && !isStandalone());

/** Whether to surface any install affordance at all (drawer entry, About row). */
export const showInstallEntry = computed(
  () =>
    !Capacitor.isNativePlatform() &&
    !isStandalone() &&
    (canInstall.value || canShowIosGuide.value || canShowMacGuide.value),
);

/**
 * Which face the install modal should wear, shared by every open site
 * (daily promo host, app drawer, About page).
 */
export const installMode: ComputedRef<'prompt' | 'ios-guide' | 'mac-guide'> = computed(() => {
  if (canInstall.value) return 'prompt';
  if (canShowIosGuide.value) return 'ios-guide';
  return 'mac-guide';
});

/**
 * Start listening for the install prompt.
 *
 * Must run at module scope in main.ts: Chromium can fire
 * `beforeinstallprompt` before the Vue app has mounted, and a missed event
 * cannot be recovered.
 */
export function initInstallCapture(): void {
  if (typeof window === 'undefined' || Capacitor.isNativePlatform()) return;

  window.addEventListener('beforeinstallprompt', (event) => {
    // Suppress Chromium's own mini-infobar; we offer install in our UI instead.
    event.preventDefault();
    deferred.value = event as BeforeInstallPromptEvent;
  });

  window.addEventListener('appinstalled', () => {
    deferred.value = null;
    installedThisSession.value = true;
    // Nothing left to promote.
    markInstallPromoDone();
  });
}

/** Show the native install sheet. The captured event can only be used once. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferred.value;
  if (!event) return 'unavailable';

  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome;
  } catch {
    return 'unavailable';
  } finally {
    deferred.value = null;
  }
}

/* ── Once-per-day promotion ──────────────────────────────────────────────── */

const PROMO_KEY = 'syntry.pwa_install';

interface InstallPromoState {
  /** Epoch ms of the first authenticated session we saw. */
  firstSeen: number;
  /** Count of authenticated app loads. */
  sessions: number;
  /**
   * Terminal stop: the app was installed (`appinstalled`), or the user chose
   * "Don't ask me again". The daily promo never shows once this is set.
   */
  promoDone: boolean;
  /** Local YYYY-MM-DD of the last promo shown — the once-per-day gate. */
  lastPrompted: string;
}

/** Local calendar date as YYYY-MM-DD — deliberately NOT UTC/toISOString. */
function localToday(): string {
  const now = new Date();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${m}-${d}`;
}

function load(): InstallPromoState {
  try {
    const raw = localStorage.getItem(PROMO_KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<InstallPromoState>) : {};
    const state: InstallPromoState = {
      firstSeen: typeof parsed.firstSeen === 'number' ? parsed.firstSeen : 0,
      sessions: typeof parsed.sessions === 'number' ? parsed.sessions : 0,
      promoDone: parsed.promoDone === true,
      lastPrompted: typeof parsed.lastPrompted === 'string' ? parsed.lastPrompted : '',
    };
    // Legacy migration: the once-ever build set promoDone on ANY dismissal, so
    // a stored promoDone with no lastPrompted predates the daily era — reset it
    // so those users re-enter the daily pool. Chromium users who actually
    // installed stay protected regardless (beforeinstallprompt never refires
    // when installed, so showInstallEntry is false); Safari users who installed
    // get one more prompt and can tap "Don't ask me again".
    if (state.promoDone && typeof parsed.lastPrompted !== 'string') {
      state.promoDone = false;
    }
    return state;
  } catch {
    return { firstSeen: 0, sessions: 0, promoDone: false, lastPrompted: '' };
  }
}

const promo = reactive<InstallPromoState>(load());

/** @returns false when localStorage is unavailable (private mode / quota). */
function persist(): boolean {
  try {
    localStorage.setItem(PROMO_KEY, JSON.stringify({ ...promo }));
    return true;
  } catch {
    return false;
  }
}

/** Call once per app load, on the transition into an authenticated session. */
export function recordAuthedSession(): void {
  if (promo.firstSeen === 0) promo.firstSeen = Date.now();
  promo.sessions += 1;
  persist();
}

/**
 * Stamp today as prompted — call BEFORE showing the promo, and skip showing
 * when this returns false: if we can't remember having asked, asking would
 * repeat on every load (same discipline as UpdatePromptHost).
 */
export function markInstallPromptShown(): boolean {
  promo.lastPrompted = localToday();
  return persist();
}

/** Stop the promo for good — the app is installed, or the user opted out. */
export function markInstallPromoDone(): void {
  if (promo.promoDone) return;
  promo.promoDone = true;
  persist();
}

/**
 * All the pacing conditions for showing today's promo.
 *
 * `localToday()` is not reactive, so this re-evaluates on app load / auth
 * transitions rather than at midnight — a tab left open across midnight won't
 * pop the modal mid-use, which is the intended "first open of the day" feel.
 */
export const installPromoEligible: ComputedRef<boolean> = computed(
  () => !promo.promoDone && promo.lastPrompted !== localToday() && showInstallEntry.value,
);
